@if (inline) {
  <nz-input-group
    [nzSize]="size"
    [nzSuffix]="CalendarIconButton"
    nz-popover
    [(nzPopoverVisible)]="popoverVisible"
    (nzPopoverVisibleChange)="popoverVisibilityChanged()"
    nzPopoverTrigger="click"
    [nzPopoverContent]="CalendarTemplate"
    nzPopoverOverlayClassName="date-picker-calendar-popover"
  >
    <ng-container [ngTemplateOutlet]="InlineInput" />
  </nz-input-group>
  <ng-template #CalendarIconButton>
    <nz-icon nzType="calendar" nzTheme="outline" />
  </ng-template>
} @else {
  <ng-container [ngTemplateOutlet]="CalendarTemplate" />
}

<!-- TEMPLATES -->
<ng-template #CalendarTemplate>
  <div
    class="calendar-wrapper"
    [class.large]="isRangeMode && allowTime"
    [class.borderless]="borderless"
  >
    <div class="single-mode-section">
      <ng-container [ngTemplateOutlet]="HeaderNavigation"></ng-container>
      <ng-container [ngTemplateOutlet]="Weekdays"></ng-container>
      <ng-container [ngTemplateOutlet]="Calendar"></ng-container>
      <ng-container [ngTemplateOutlet]="Buttons"></ng-container>
    </div>

    @if (isSingleMode && allowTime) {
      <ng-container [ngTemplateOutlet]="TimeInput"></ng-container>
    }

    @if (isRangeMode && allowTime) {
      <ng-container [ngTemplateOutlet]="StartEndTimeInputs"></ng-container>
    }
  </div>
</ng-template>

<ng-template #InlineInput>
  @if (isSingleMode) {
    <input
      type="text"
      nz-input
      [ngModel]="formatSingleDate()"
      [placeholder]="inlinePlaceholder"
      readonly
      nzType="primary"
      [nzSize]="size"
    />
  } @else {
    <input
      type="text"
      nz-input
      [ngModel]="formatStartDate()"
      [placeholder]="'Start date'"
      readonly
      nzType="primary"
      [nzSize]="size"
    />
    <input
      type="text"
      nz-input
      [ngModel]="formatEndDate()"
      [placeholder]="'End date'"
      readonly
      nzType="primary"
      [nzSize]="size"
    />
  }
</ng-template>

<!-- Header with date navigation -->
<ng-template #HeaderNavigation>
  <div
    class="inline-flex w-full justify-between items-center px-3 py-[6px] border-b-[1px] border-solid border-[#f0f0f0]"
  >
    <div class="inline-flex gap-2 w-full">
      <button
        nz-button
        nzType="default"
        nzSize="small"
        (click)="previousMonth()"
        nz-tooltip
        nzTooltipTitle="Previous month"
      >
        <nz-icon nzType="left" nzTheme="outline" />
      </button>

      <nz-select
        [ngModel]="currentMonth.month()"
        (ngModelChange)="changeMonth($event)"
        nzSize="small"
        class="!w-28"
      >
        <nz-option
          *ngFor="let m of months; let i = index"
          [nzValue]="i"
          [nzLabel]="m"
        >
        </nz-option>
      </nz-select>

      <button
        nz-button
        nzType="default"
        nzSize="small"
        (click)="nextMonth()"
        nz-tooltip
        nzTooltipTitle="Next month"
      >
        <nz-icon nzType="right" nzTheme="outline" />
      </button>

      <nz-select
        [ngModel]="currentMonth.year()"
        (ngModelChange)="changeYear($event)"
        nzSize="small"
        class="!w-20"
      >
        <nz-option *ngFor="let y of years" [nzValue]="y" [nzLabel]="y">
        </nz-option>
      </nz-select>
    </div>
  </div>
</ng-template>

<!-- Weekdays -->
<ng-template #Weekdays>
  <div class="weekdays">
    <span *ngFor="let day of weekdays">{{ day }}</span>
  </div>
</ng-template>

<!-- Calendar -->
<ng-template #Calendar>
  <div class="calendar">
    <div class="week" *ngFor="let week of weeks">
      <div
        class="day"
        *ngFor="let day of week"
        [class.disabled]="isDisabled(day)"
        [class.weekend]="isWeekend(day)"
        [class.outside]="!isCurrentMonth(day)"
        [class.today]="day.isSame(moment(), 'day')"
        [class.selected]="
          day.isSame(startDate, 'day') ||
          day.isSame(endDate, 'day') ||
          day.isSame(singleDate, 'day')
        "
        [class.in-range]="isInRange(day)"
        [class.start-incomplete]="
          isStartDay(day) && !endDate && allowSelectingBothDirections
        "
        [class.start]="isStartDay(day)"
        [class.end]="isEndDay(day)"
        [class.hover-range]="isInHoverRange(day)"
        (mouseenter)="onDayHover(day)"
        (mouseleave)="onDayLeave()"
        (click)="selectDate(day)"
      >
        {{ day.date() }}
      </div>
    </div>
  </div>
</ng-template>

<!-- Buttons -->
<ng-template #Buttons>
  @if (allowToday || allowToday || isRangeMode) {
    <div class="inline-flex justify-end gap-1 w-full px-3 pt-1 pb-2">
      @if (allowToday) {
        <button nz-button nzType="link" nzSize="small" (click)="goToday()">
          Today
        </button>
      }
      @if (allowClear) {
        <button
          nz-button
          nzType="default"
          nzSize="small"
          (click)="clearSelection()"
        >
          Clear
        </button>
      }
      @if (isRangeMode) {
        <button
          nz-button
          nzType="primary"
          nzSize="small"
          (click)="apply()"
          [disabled]="!canApply"
        >
          Apply
        </button>
      }
    </div>
  }
</ng-template>

<ng-template #TimeInput>
  <div class="large-mode">
    <div
      class="inline-flex justify-between items-center px-3 py-[6px] border-b-[1px] border-solid border-[#f0f0f0] h-[37px]"
    >
      <div class="inline-flex items-center gap-2 h-[37px]">
        <nz-icon nzType="clock-circle" nzTheme="outline" /> Time slot
      </div>
    </div>
    <div class="inline-flex flex-col justify-between px-3 w-full h-full">
      <div class="w-full">
        @if (timePickerMode === 'dropdown') {
          <div
            class="inline-flex w-full flex-col bg-white p-1 rounded-sm text-center border-solid border-[1px] shadow-sm cursor-pointer"
            [ngStyle]="{
              'border-color': nzOpenSingleTime ? '#1790FF' : '#f0f0f0',
            }"
            (click)="toggleTimePicker('singleTime')"
          >
            <label class="font-semibold">Slot</label>
            <div class="inline-flex w-full">
              <nz-select
                [(ngModel)]="singleTime"
                nzSize="small"
                nzBorderless
                class="w-full"
                [nzShowArrow]="false"
                [nzOpen]="nzOpenSingleTime"
                (ngModelChange)="nzOpenSingleTime = false; validateSingleTime()"
              >
                <nz-option
                  *ngFor="let option of timePickerOptions.slice(0, -1)"
                  [nzValue]="option"
                  [nzLabel]="option"
                />
              </nz-select>
            </div>
          </div>
        } @else {
          <div class="inline-flex w-full">
            <nz-input-number
              [(ngModel)]="singleHour"
              nzSize="small"
              nzMin="0"
              nzMax="23"
              [disabled]="!singleDate"
              class="!w-14"
              nzStepperless
              (focusout)="validateSingleTime()"
            />
            <div class="border-[#D9D9D9] border-solid border-y-[1px] px-1">
              :
            </div>
            <nz-input-number
              [(ngModel)]="singleMinute"
              nzSize="small"
              nzMin="0"
              nzMax="59"
              [disabled]="!singleDate"
              class="!w-14"
              nzStepperless
              (focusout)="validateSingleTime()"
            />
          </div>
        }
      </div>

      @if (showDuration && duration) {
        <div class="py-[10px]">
          <small> Duration of {{ duration }} </small>
        </div>
      }
    </div>
  </div>
</ng-template>

<ng-template #StartEndTimeInputs>
  <div class="large-mode">
    <div
      class="inline-flex justify-between items-center px-3 py-[6px] border-b-[1px] border-solid border-[#f0f0f0] h-[37px]"
    >
      <div class="inline-flex items-center gap-2 h-[37px]">
        <nz-icon nzType="clock-circle" nzTheme="outline" /> Time slot
      </div>
    </div>
    <div class="px-3 w-full h-full">
      <div class="inline-flex flex-col justify-between w-full h-full">
        <div class="inline-flex flex-col justify-between gap-2 w-full">
          <div
            class="inline-flex flex-col bg-white p-1 rounded-sm text-center border-solid border-[1px] shadow-sm cursor-pointer"
            [ngStyle]="{
              'border-color': nzOpenStartTime ? '#1790FF' : '#f0f0f0',
            }"
            (click)="toggleTimePicker('startTime')"
          >
            <ng-container [ngTemplateOutlet]="StartTime"></ng-container>
          </div>

          <div
            class="inline-flex flex-col w-full bg-white p-1 rounded-sm text-center border-solid border-[1px] shadow-sm cursor-pointer"
            [ngStyle]="{
              'border-color': nzOpenEndTime ? '#1790FF' : '#f0f0f0',
            }"
            (click)="toggleTimePicker('endTime')"
          >
            <ng-container [ngTemplateOutlet]="EndTime"></ng-container>
          </div>
        </div>

        @if (showDuration && duration) {
          <div class="py-[10px]">
            <small> Duration of {{ duration }} </small>
          </div>
        }
      </div>
    </div>
  </div>
</ng-template>

<!-- Start Time -->
<ng-template #StartTime>
  <label class="font-semibold">Start</label>
  <div class="inline-flex w-full">
    @if (timePickerMode === 'dropdown') {
      <nz-select
        [(ngModel)]="startTime"
        nzSize="small"
        nzBorderless
        class="w-full"
        [nzShowArrow]="false"
        [nzOpen]="nzOpenStartTime"
        (ngModelChange)="nzOpenStartTime = false; validateStartTime()"
      >
        <nz-option
          *ngFor="let option of timePickerOptions.slice(0, -1)"
          [nzValue]="option"
          [nzLabel]="option"
        />
      </nz-select>
    } @else {
      <nz-input-number
        [(ngModel)]="startHour"
        nzSize="small"
        nzMin="0"
        nzMax="23"
        class="!w-14"
        nzStepperless
        (focusout)="validateStartTime()"
      />
      <div class="border-[#D9D9D9] border-solid border-y-[1px] px-1">:</div>
      <nz-input-number
        [(ngModel)]="startMinute"
        nzSize="small"
        nzMin="0"
        nzMax="59"
        class="!w-14"
        nzStepperless
        (focusout)="validateStartTime()"
      />
    }
  </div>
</ng-template>

<!-- End Time -->
<ng-template #EndTime>
  <label class="font-semibold">End</label>
  <div class="inline-flex w-full">
    @if (timePickerMode === 'dropdown') {
      <nz-select
        [(ngModel)]="endTime"
        nzSize="small"
        nzBorderless
        class="w-full"
        [nzOpen]="nzOpenEndTime"
        [nzShowArrow]="false"
        (ngModelChange)="nzOpenEndTime = false"
      >
        @for (option of timePickerOptions; track option) {
          @if (!isBeforeStartTime(option)) {
            <nz-option [nzValue]="option" [nzLabel]="option" />
          }
        }
      </nz-select>
    } @else {
      <nz-input-number
        [(ngModel)]="endHour"
        nzSize="small"
        nzMin="0"
        nzMax="23"
        class="!w-14"
        nzStepperless
        (focusout)="validateEndTime()"
      />
      <div class="border-[#D9D9D9] border-solid border-y-[1px] px-1">:</div>
      <nz-input-number
        [(ngModel)]="endMinute"
        nzSize="small"
        nzMin="0"
        nzMax="59"
        class="!w-14"
        nzStepperless
        (focusout)="validateEndTime()"
      />
    }
  </div>
</ng-template>
